import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { UserCheck } from 'lucide-react';
import {
  formatDate,
  formatInteger,
  formatPhoneDisplay,
  importRowFilters,
  importRowStatusLabels,
  type CallListType,
  type ImportRowFilter,
} from '@brimo/shared';
import { Banner, Button, FilterChip, IconButton, Skeleton, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { usePermissions } from '../../../lib/session';
import {
  promoteListContact,
  removeListContact,
  useListContacts,
  useListDetail,
  type ListContactRow,
  type ListDetail,
} from '../api';
import { Crumb, DialerPage, Eyebrow, LoadError } from '../ui';
import { AddContactsDialog } from './AddContactsDialog';
import { CampaignPriorityCard } from './CampaignPriorityCard';
import { FilterCard } from './FilterCard';
import { ImportCard } from './ImportCard';
import { SettingsCard } from './SettingsCard';
import { SourcesCard } from './SourcesCard';
import styles from './lists.module.css';

const NOTES: Record<CallListType, string> = {
  smart:
    'Der Bestand ergibt sich aus den Filtern und ändert sich mit den Daten. Kontakte lassen sich hier nicht einzeln entfernen — dafür die Bedingungen anpassen.',
  manual:
    'Kontakte werden von Hand gepflegt. Angewählt werden nur Neu, Kontaktiert und Follow Up — alles andere bleibt in der Liste und wird übersprungen.',
  campaign:
    'Die Liste füllt sich automatisch, sobald Leads über Webhook oder API eingehen. Neue Kontakte rutschen in eine laufende Queue nach.',
  import:
    'Neue Personen aus der Datei sind Importkontakte: Sie stehen nicht in der Kontaktliste und werden erst übernommen, wenn ein Setter sie erreicht. Beim Abschließen der Liste werden nie erreichte Importkontakte gelöscht.',
};

function countLine(list: { type: CallListType; total: number; dialable: number }): string {
  const total = formatInteger(list.total);
  const dialable = formatInteger(list.dialable);
  if (list.type === 'smart') return `${total} Treffer · ${dialable} jetzt wählbar`;
  return `${total} ${list.total === 1 ? 'Kontakt' : 'Kontakte'} · ${dialable} jetzt wählbar`;
}

/**
 * Terminierung 05/06/07 — one list: header (type, name, note, „Einstellungen", „Queue starten · N"), the
 * table „Kontakte in der Liste" with the reason of every skipped contact, and on the right the filter editor
 * (smart view), the sources (campaign) and the dialer settings. Only manual lists add and remove contacts.
 */
export function ListDetailPage() {
  const params = useParams();
  const id = params['id'] ?? '';
  const detail = useListDetail(id);
  const list = detail.data;
  const shortName = list ? (list.name.split(' · ')[0] ?? list.name) : '…';
  const crumb = (
    <Crumb>
      <Link to="/dialer/lists" className={styles.crumbLink}>
        Kontakte · Listen
      </Link>{' '}
      · {shortName}
    </Crumb>
  );

  if (detail.isError) {
    return (
      <DialerPage crumb={crumb}>
        <LoadError
          error={detail.error}
          what="Die Liste"
          retrying={detail.isFetching}
          onRetry={() => void detail.refetch()}
        />
      </DialerPage>
    );
  }

  return <DialerPage crumb={crumb}>{list ? <ListDetailBody list={list} /> : <DetailSkeleton />}</DialerPage>;
}

function DetailSkeleton() {
  return (
    <div className={styles.detailPage} aria-busy="true">
      <div className={styles.detailHeader}>
        <div>
          <Skeleton width={100} height={11} />
          <div className={styles.detailTitle}>
            <Skeleton width={320} height={30} />
          </div>
        </div>
      </div>
      <div className={styles.detailGrid}>
        <div className={styles.card}>
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className={styles.contactRow}>
              <Skeleton width={200} height={14} />
            </div>
          ))}
        </div>
        <div className={styles.sideCard}>
          <Skeleton width="100%" height={160} />
        </div>
      </div>
    </div>
  );
}

function ListDetailBody({ list }: { list: ListDetail }) {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLElement>(null);
  const canManageCampaign = permissions.has('settings.organization');

  function openSettings() {
    setSettingsOpen(true);
    settingsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    settingsRef.current?.focus({ preventScroll: true });
  }

  return (
    <div className={styles.detailPage}>
      <div className={styles.detailHeader}>
        <div>
          <Eyebrow>{list.typeLabel}</Eyebrow>
          <h2 className={styles.detailTitle}>{list.name}</h2>
          <p className={styles.detailNote}>{NOTES[list.type]}</p>
        </div>
        <div className={styles.detailActions}>
          <button type="button" className={styles.secondary} onClick={openSettings}>
            Einstellungen
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={!list.active}
            title={list.active ? undefined : 'Diese Liste ist nicht als Dialer-Liste eingeschaltet.'}
            onClick={() => void navigate(`/dialer/preview/${list.id}`)}
          >
            Queue starten · {list.dialable}
          </button>
        </div>
      </div>
      {!list.active ? (
        <Banner tone="neutral">
          Diese Liste ist nicht als Dialer-Liste eingeschaltet. Schalten Sie sie unter Kontakte › Listen ein.
        </Banner>
      ) : null}
      <div className={styles.detailGrid}>
        <ContactsCard list={list} onAdd={() => setAdding(true)} />
        <div className={styles.side}>
          {list.type === 'import' && list.import ? <ImportCard list={list} summary={list.import} /> : null}
          {list.type === 'smart' ? <FilterCard list={list} /> : null}
          {list.type === 'campaign' ? <SourcesCard list={list} canManage={canManageCampaign} /> : null}
          {list.type === 'campaign' ? (
            <CampaignPriorityCard list={list} canManage={canManageCampaign} />
          ) : null}
          <SettingsCard
            ref={settingsRef}
            list={list}
            expanded={settingsOpen}
            onExpandedChange={setSettingsOpen}
          />
        </div>
      </div>
      {list.type === 'manual' ? (
        <AddContactsDialog
          open={adding}
          listId={list.id}
          onClose={() => setAdding(false)}
          onAdded={(message) => toast({ message })}
        />
      ) : null}
    </div>
  );
}

function ContactsCard({ list, onAdd }: { list: ListDetail; onAdd: () => void }) {
  const [status, setStatus] = useState<ImportRowFilter>('all');
  const imported = list.type === 'import';
  const contacts = useListContacts(list.id, imported ? status : 'all');
  const pages = contacts.data?.pages ?? [];
  const first = pages[0];
  const rows = pages.flatMap((page) => page.rows);
  const manual = list.type === 'manual';
  const counts = first ?? { total: list.total, dialable: list.dialable };

  return (
    <section className={styles.card} aria-label="Kontakte in der Liste">
      <div className={styles.cardHead}>
        <Eyebrow>Kontakte in der Liste</Eyebrow>
        <span className={styles.cardHeadNote}>{countLine({ type: list.type, ...counts })}</span>
        {manual && list.canEdit ? (
          <button type="button" className={styles.dashed} onClick={onAdd}>
            Kontakte hinzufügen
          </button>
        ) : null}
      </div>
      {imported && first?.statusCounts ? (
        <div className={styles.statusFilters} role="group" aria-label="Nach Status filtern">
          {importRowFilters.map((filter) => {
            const statusCounts = first.statusCounts;
            const count =
              statusCounts === null
                ? 0
                : filter === 'all'
                  ? Object.values(statusCounts).reduce((sum, n) => sum + n, 0)
                  : statusCounts[filter];
            return (
              <FilterChip
                key={filter}
                label={filter === 'all' ? 'Alle' : importRowStatusLabels[filter]}
                count={formatInteger(count)}
                active={status === filter}
                onClick={() => setStatus(filter)}
              />
            );
          })}
        </div>
      ) : null}
      {first?.rowsHidden ? (
        <p className={styles.hiddenRows}>
          Als Setter sehen Sie die Zahlen dieser Liste, nicht die einzelnen Kontakte.
        </p>
      ) : (
        <>
          <div className={styles.contactsHead} aria-hidden="true">
            <div>Kontakt</div>
            <div className={styles.entryCell}>Eingang</div>
            <div>Status</div>
            <div>Letzter Versuch</div>
            <div>Wählbar</div>
            <div />
          </div>
          {contacts.isError ? (
            <div className={styles.moreRow}>
              <LoadError
                error={contacts.error}
                what="Die Kontakte"
                retrying={contacts.isFetching}
                onRetry={() => void contacts.refetch()}
              />
            </div>
          ) : contacts.isPending ? (
            Array.from({ length: 6 }, (_, index) => (
              <div key={index} className={styles.contactRow}>
                <Skeleton width={180} height={14} />
              </div>
            ))
          ) : rows.length === 0 ? (
            <p className={styles.hiddenRows}>
              {manual
                ? 'Noch keine Kontakte in dieser Liste.'
                : imported
                  ? 'Keine Kontakte mit diesem Status.'
                  : 'Gerade trifft kein Kontakt auf diese Liste zu.'}
            </p>
          ) : (
            rows.map((row) => (
              <ContactRow key={row.contactId} row={row} listId={list.id} removable={manual && list.canEdit} />
            ))
          )}
          {contacts.hasNextPage ? (
            <div className={styles.moreRow}>
              <Button
                variant="secondary"
                size="small"
                loading={contacts.isFetchingNextPage}
                onClick={() => void contacts.fetchNextPage()}
              >
                Weitere laden
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function ContactRow({ row, listId, removable }: { row: ListContactRow; listId: string; removable: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pending, setPending] = useState(false);

  async function promote() {
    setPending(true);
    try {
      await promoteListContact(listId, row.contactId);
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
      toast({ message: `${row.name} ist jetzt ein Kontakt.` });
    } catch (caught) {
      toast({ message: errorMessage(caught) });
    } finally {
      setPending(false);
    }
  }

  async function remove() {
    setPending(true);
    try {
      await removeListContact(listId, row.contactId);
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
    } catch (caught) {
      toast({ message: errorMessage(caught) });
    } finally {
      setPending(false);
    }
  }

  return (
    <div className={styles.contactRow}>
      <div className={styles.contactName}>
        <Link to={`/contacts/${row.contactId}`} className={styles.contactLink}>
          {row.name}
        </Link>
        {row.phone ? <div className={styles.contactPhone}>{formatPhoneDisplay(row.phone)}</div> : null}
      </div>
      <div className={styles.entryCell}>{formatDate(row.createdAt)}</div>
      <div>
        <span className={styles.statusPill}>{row.import ? row.import.statusLabel : row.lifecycle.label}</span>
      </div>
      <div className={styles.lastAttempt}>{row.lastAttempt}</div>
      <div>
        {row.dialable === true ? (
          <span className={styles.dialableYes}>ja</span>
        ) : (
          <span className={styles.dialableNo}>
            {row.dialable.reason}
            {row.dialable.detail ? (
              <span className={styles.dialableDetail}>{row.dialable.detail}</span>
            ) : null}
          </span>
        )}
      </div>
      <div className={styles.removeCell}>
        {row.import?.canPromote ? (
          <IconButton
            icon={UserCheck}
            label={`${row.name} als Kontakt übernehmen`}
            variant="ghost"
            size={28}
            disabled={pending}
            onClick={() => void promote()}
          />
        ) : removable ? (
          <button
            type="button"
            className={styles.remove}
            aria-label={`${row.name} aus der Liste entfernen`}
            disabled={pending}
            onClick={() => void remove()}
          >
            ×
          </button>
        ) : null}
      </div>
    </div>
  );
}
